import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle,
  ArrowRight,
  Sparkle,
  Crown,
  Diamond,
  Medal,
  Lightning,
  Info,
} from "@phosphor-icons/react";
import PageHero from "../components/site/PageHero";
import { PACKAGES, APP_URLS } from "../data/site";

const tierIcon = {
  silver: Medal,
  gold: Sparkle,
  diamond: Diamond,
  platinum: Crown,
};

export default function Packages() {
  return (
    <div data-testid="packages-page">
      <PageHero
        eyebrow="JayesPay Packages"
        title="Pick the plan that fits"
        highlight="your shop"
        description="From a free starter package to a full retail OS — every JayesPay package unlocks more services, higher margins and broader reach."
        testId="packages-hero"
      />

      {/* Pricing grid */}
      <section data-testid="packages-pricing-section" className="pt-12 sm:pt-16 lg:pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {PACKAGES.map((pkg, i) => {
              const Icon = tierIcon[pkg.id] || Sparkle;
              const isDark = pkg.isDark;
              const isFeatured = pkg.id === "diamond";
              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: i * 0.07 }}
                  data-testid={`package-card-${pkg.id}`}
                  className={`relative flex flex-col rounded-3xl p-7 transition-all hover:-translate-y-1 ${
                    isDark
                      ? "bg-ink900 text-surface border border-brandBlue/40 shadow-[0_30px_60px_-25px_rgba(11,64,120,0.5)]"
                      : "bg-surfaceAlt border border-line hover:shadow-xl hover:border-lineStrong"
                  } ${isFeatured ? "ring-2 ring-brandBlue ring-offset-4 ring-offset-surface" : ""}`}
                >
                  {pkg.badge && (
                    <div
                      className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-bold ${
                        isFeatured
                          ? "bg-brandBlue text-surfaceAlt"
                          : "bg-brandOrange text-ink900"
                      }`}
                    >
                      {pkg.badge}
                    </div>
                  )}

                  <div
                    className={`h-12 w-12 rounded-xl flex items-center justify-center mb-5 ${
                      isDark ? "brand-icon-tile-dark" : "brand-icon-tile"
                    }`}
                  >
                    <Icon size={22} weight="duotone" />
                  </div>

                  <h3
                    className={`font-heading font-extrabold text-2xl tracking-tight mb-1 ${
                      isDark ? "text-surface" : "text-ink900"
                    }`}
                  >
                    {pkg.name}
                  </h3>
                  <p
                    className={`text-sm mb-5 ${
                      isDark ? "text-surface/65" : "text-ink500"
                    }`}
                  >
                    {pkg.tagline}
                  </p>

                  <div className="flex items-baseline gap-1.5 mb-1">
                    <span
                      className={`font-heading font-black text-4xl tracking-tighter ${
                        isDark ? "text-surface" : "text-ink900"
                      }`}
                    >
                      {pkg.price}
                    </span>
                    <span
                      className={`text-xs font-semibold ${
                        isDark ? "text-surface/65" : "text-ink500"
                      }`}
                    >
                      / {pkg.period}
                    </span>
                  </div>
                  <div
                    className={`text-[11px] uppercase tracking-[0.18em] font-semibold mb-6 ${
                      isDark ? "text-brandOrange" : "text-infoText"
                    }`}
                  >
                    {pkg.priceNote}
                  </div>

                  <a
                    href={APP_URLS.register}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`package-${pkg.id}-cta`}
                    className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-bold mb-6 transition-colors ${
                      isFeatured
                        ? "bg-brandBlue text-surfaceAlt hover:bg-brandNavy"
                        : isDark
                        ? "bg-brandOrange text-ink900 hover:bg-surface"
                        : "bg-brandNavy text-surfaceAlt hover:bg-brandBlue"
                    }`}
                  >
                    Activate {pkg.name.replace("JayesPay ", "")}
                    <ArrowRight size={14} weight="bold" />
                  </a>

                  <div
                    className={`text-[10px] uppercase tracking-[0.2em] font-bold mb-3 ${
                      isDark ? "text-brandOrange" : "text-ink500"
                    }`}
                  >
                    What you get
                  </div>
                  <ul className="space-y-2.5 flex-1">
                    {pkg.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm leading-snug"
                      >
                        <CheckCircle
                          size={16}
                          weight="fill"
                          className={`mt-0.5 flex-shrink-0 ${
                            isDark ? "text-sky-200" : "text-brandBlue"
                          }`}
                        />
                        <span
                          className={isDark ? "text-surface/90" : "text-ink900"}
                        >
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Notes / fine print */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="bg-infoBg/50 border border-line rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row gap-4 items-start">
            <div className="h-10 w-10 rounded-xl bg-infoBg flex items-center justify-center flex-shrink-0">
              <Info size={20} weight="fill" className="text-infoText" />
            </div>
            <div className="text-sm text-infoText leading-relaxed space-y-2">
              <p>
                <strong className="text-ink900">Activation fees</strong> are
                one-time, GST applicable. Packages unlock service categories;
                each transaction also earns a per-service commission visible
                inside the retailer app.
              </p>
              <p>
                Packages can be upgraded any time from the in-app Package
                screen. Service availability is subject to KYC verification and
                partner-bank/operator approval.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature compare highlight */}
      <section className="py-16 bg-surfaceAlt border-y border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="max-w-3xl mb-10">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-infoText mb-3">
              Which package is right for you?
            </div>
            <h2 className="font-heading font-extrabold text-ink900 text-3xl sm:text-4xl tracking-tight">
              Three quick recommendations.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[
              {
                title: "Just starting out?",
                pkg: "JayesPay Silver",
                text: "Start free with core recharge & bill-pay services. Upgrade once your customer flow grows.",
                tag: "silver",
              },
              {
                title: "Already serving 50+ customers/day?",
                pkg: "JayesPay Diamond",
                text: "Unlock DMT, AePS, m-ATM, Mobile POS and Aadhaar Pay — the workhorses of retail fintech.",
                tag: "diamond",
              },
              {
                title: "Running a full counter / sub-distributor?",
                pkg: "JayesPay Platinum",
                text: "Travel, loans, current accounts, BC point and Silver/Gold buy-sell — the complete retail OS.",
                tag: "platinum",
              },
            ].map((r, i) => (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="bg-surface border border-line rounded-2xl p-6 hover:border-brandBlue/40 transition-colors"
                data-testid={`recommendation-${r.tag}`}
              >
                <div className="h-10 w-10 rounded-xl brand-icon-tile flex items-center justify-center mb-4">
                  <Lightning size={20} weight="duotone" className="text-ink900" />
                </div>
                <div className="text-sm font-bold text-ink900 mb-1">{r.title}</div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-infoText mb-3">
                  Recommended: {r.pkg}
                </div>
                <p className="text-sm text-ink700 leading-relaxed">{r.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/become-partner"
              data-testid="packages-partner-link"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brandNavy text-surfaceAlt text-sm font-bold hover:bg-brandBlue transition-colors"
            >
              Talk to our onboarding team
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
